import { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Loader2,
  List,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { MeetingsProvider, useMeetings } from "@/providers/MeetingsProvider";
import type { MeetingDto, MeetingDashboardState } from "@shared/types/MeetingDto";
import { MeetingStatsHeader } from "./MeetingStatsHeader";
import { MeetingsList } from "./MeetingsList";
import { MeetingCalendarView } from "./MeetingCalendarView";
import { MeetingDetailSheet } from "./MeetingDetailSheet";
import { CancelMeetingDialog } from "./CancelMeetingDialog";
import { RescheduleMeetingDialog } from "./RescheduleMeetingDialog";
import { CreateMeetingDialog } from "./CreateMeetingDialog";
import { TYPE_LABELS } from "./constants";

const TABS: { id: MeetingDashboardState["activeTab"]; label: string }[] = [
  { id: "UPCOMING", label: "Upcoming" },
  { id: "PENDING", label: "Pending" },
  { id: "NO_SHOW", label: "No-show" },
];

type ViewMode = "list" | "calendar";

function MeetingsPageContent() {
  const location = useLocation();
  const {
    filteredMeetings,
    isLoading,
    error,
    dashboardState,
    setDashboardState,
    selectedMeeting,
    setSelectedMeeting,
    fetchMeetings,
  } = useMeetings();

  const [highlightMeetingId, setHighlightMeetingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  useEffect(() => {
    const state = location.state as { highlightMeetingId?: string } | null;
    if (state?.highlightMeetingId) {
      setHighlightMeetingId(state.highlightMeetingId);
      window.history.replaceState({}, "");
      const timer = setTimeout(() => setHighlightMeetingId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actionMeeting, setActionMeeting] = useState<MeetingDto | null>(null);

  const handleViewMeeting = useCallback(
    (meeting: MeetingDto) => {
      setSelectedMeeting(meeting);
      setDetailOpen(true);
    },
    [setSelectedMeeting]
  );

  const handleCancelMeeting = useCallback((meeting: MeetingDto) => {
    setActionMeeting(meeting);
    setCancelDialogOpen(true);
  }, []);

  const handleRescheduleMeeting = useCallback((meeting: MeetingDto) => {
    setActionMeeting(meeting);
    setRescheduleDialogOpen(true);
  }, []);

  return (
    <div className="w-full">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold mb-2">
            Meetings
          </h1>
          <MeetingStatsHeader />
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Request a meeting
        </Button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search meetings..."
              value={dashboardState.searchQuery || ""}
              onChange={(e) =>
                setDashboardState({ searchQuery: e.target.value })
              }
              className="pl-9"
            />
          </div>

          <Select
            value={dashboardState.filterByType || "ALL"}
            onValueChange={(v) =>
              setDashboardState({
                filterByType: v as MeetingDashboardState["filterByType"],
              })
            }
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center border border-border rounded-md overflow-hidden ml-auto">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "rounded-none h-8 px-3",
                viewMode === "list" && "bg-muted"
              )}
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "rounded-none h-8 px-3",
                viewMode === "calendar" && "bg-muted"
              )}
              onClick={() => setViewMode("calendar")}
              aria-label="Calendar view"
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto pb-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setDashboardState({ activeTab: tab.id })}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors",
                dashboardState.activeTab === tab.id
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-center py-24">
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="outline" onClick={fetchMeetings}>
              Try Again
            </Button>
          </div>
        ) : (
          <motion.div
            key={`${dashboardState.activeTab}-${viewMode}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            {viewMode === "list" ? (
              <MeetingsList
                meetings={filteredMeetings}
                ghosted={dashboardState.activeTab === "NO_SHOW"}
                highlightMeetingId={highlightMeetingId}
                onView={handleViewMeeting}
                onReschedule={handleRescheduleMeeting}
                onCancel={handleCancelMeeting}
              />
            ) : (
              <MeetingCalendarView
                meetings={filteredMeetings}
                onSelectMeeting={handleViewMeeting}
              />
            )}
          </motion.div>
        )}
      </div>

      <MeetingDetailSheet
        meeting={selectedMeeting}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onReschedule={handleRescheduleMeeting}
        onCancel={handleCancelMeeting}
      />

      <CancelMeetingDialog
        meeting={actionMeeting}
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
      />

      <RescheduleMeetingDialog
        meeting={actionMeeting}
        open={rescheduleDialogOpen}
        onOpenChange={setRescheduleDialogOpen}
      />

      <CreateMeetingDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}

export function MeetingsPage() {
  return (
    <MeetingsProvider>
      <MeetingsPageContent />
    </MeetingsProvider>
  );
}
